import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { currencyLabel } from "@/lib/currency";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, Wand2, Zap, Mic, Square, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const CATEGORY_IDS = ["materiale", "manopera", "transport", "altele"];

const emptyItem = () => ({ description: "", category: "materiale", quantity: 1, unit: "buc", unit_price: 0 });

export default function OfferNew() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const cur = currencyLabel(i18n.language);
  const catLabel = { materiale: t("offerNew.catMateriale"), manopera: t("offerNew.catManopera"), transport: t("offerNew.catTransport"), altele: t("offerNew.catAltele") };
  const { refresh, user } = useAuth();
  const hasVoiceAccess = user?.is_admin || ["pro", "business"].includes(user?.plan);
  const [client_name, setClient] = useState("");
  const [client_company, setClientCompany] = useState("");
  const [client_address, setClientAddress] = useState("");
  const [project_title, setProject] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [vat_percent, setVat] = useState(19);
  const [markup_percent, setMarkup] = useState(0);
  const [notes, setNotes] = useState("");
  const [ai_intro, setAiIntro] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiItemBusy, setAiItemBusy] = useState(null);
  const [aiFullOpen, setAiFullOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiFullBusy, setAiFullBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  // Voice-to-Offer
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  // Track the visible viewport height so the AI modal stays usable when the mobile keyboard opens
  const [viewportH, setViewportH] = useState(null);
  useEffect(() => {
    if (!aiFullOpen || typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => setViewportH(vv.height);
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, [aiFullOpen]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); }, []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + (Number(i.quantity)||0) * (Number(i.unit_price)||0), 0);
    const markup = subtotal * ((Number(markup_percent)||0) / 100);
    const base = subtotal + markup;
    const vat = base * ((Number(vat_percent)||0) / 100);
    const total = base + vat;
    return { subtotal, markup, base, vat, total };
  }, [items, vat_percent, markup_percent]);

  const updateItem = (idx, key, val) => {
    setItems(items.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };
  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (idx) => setItems(items.length > 1 ? items.filter((_, i) => i !== idx) : items);

  const aiRewriteItem = async (idx) => {
    const it = items[idx];
    if (!it.description || it.description.trim().length < 2) {
      toast.error(t("offerNew.needDesc"));
      return;
    }
    setAiItemBusy(idx);
    try {
      const r = await api.post("/ai/item-description", { description: it.description, category: it.category, lang: i18n.language });
      updateItem(idx, "description", r.data.text);
      toast.success(t("offerNew.descImproved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerNew.aiError"));
    } finally {
      setAiItemBusy(null);
    }
  };

  const applyOffer = (d) => {
    setProject(d.project_title || "");
    setAiIntro(d.ai_intro || "");
    setItems(d.items && d.items.length ? d.items : [emptyItem()]);
    setVat(d.vat_percent ?? 19);
    setMarkup(d.markup_percent ?? 0);
    setNotes(d.notes || "");
  };

  const openVoice = () => {
    if (!hasVoiceAccess) {
      toast.error(t("offerNew.voiceNeedAccess"));
      nav("/app/abonament");
      return;
    }
    setSeconds(0); setRecording(false); setProcessing(false); setTranscript("");
    setVoiceOpen(true);
  };

  const cleanupRec = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const closeVoice = () => {
    if (recording && mrRef.current && mrRef.current.state !== "inactive") {
      try { mrRef.current.onstop = null; mrRef.current.stop(); } catch {}
    }
    cleanupRec();
    setVoiceOpen(false); setRecording(false); setProcessing(false);
  };

  const sendVoice = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "recording.webm");
      fd.append("lang", i18n.language);
      const r = await api.post("/ai/voice-offer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyOffer(r.data);
      setTranscript(r.data.transcript || "");
      setVoiceOpen(false);
      toast.success(t("offerNew.voiceGenerated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerNew.transcribeError"));
    } finally {
      setProcessing(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        cleanupRec();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        await sendVoice(blob);
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error(t("offerNew.micError"));
    }
  };

  const stopRec = () => {
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
    if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop();
  };

  const fmtSec = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const aiGenerateFullOffer = async () => {
    if (aiBrief.trim().length < 10) {
      toast.error(t("offerNew.briefMin"));
      return;
    }
    setAiFullBusy(true);
    try {
      const r = await api.post("/ai/generate-offer", { description: aiBrief.trim(), lang: i18n.language });
      const d = r.data;
      setProject(d.project_title || "");
      setAiIntro(d.ai_intro || "");
      setItems(d.items && d.items.length ? d.items : [emptyItem()]);
      setVat(d.vat_percent ?? 19);
      setMarkup(d.markup_percent ?? 0);
      setNotes(d.notes || "");
      setAiFullOpen(false);
      setAiBrief("");
      toast.success(t("offerNew.fullGenerated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerNew.aiError"));
    } finally {
      setAiFullBusy(false);
    }
  };

  const generateAI = async () => {
    if (!project_title || !client_name) {
      toast.error(t("offerNew.needClientProject"));
      return;
    }
    setAiBusy(true);
    try {
      const items_summary = items.filter(i=>i.description).map(i => `${i.description} (${i.category})`).join(", ");
      const r = await api.post("/ai/offer-text", { project_title, client_name, items_summary, lang: i18n.language });
      setAiIntro(r.data.text);
      toast.success(t("offerNew.textGenerated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerNew.aiError"));
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        client_name, client_company, client_address, project_title,
        items: items.map(it => ({...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price)})),
        vat_percent: Number(vat_percent),
        markup_percent: Number(markup_percent),
        notes, ai_intro,
      };
      const r = await api.post("/offers", body);
      toast.success(t("offerNew.offerCreated"));
      refresh();
      nav(`/app/oferte/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offerNew.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="offer-new-form">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">{t("offerNew.overline")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("offerNew.pageTitle")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openVoice}
            className="btn-outline flex items-center gap-2 !py-3"
            data-testid="voice-offer-btn"
            title={t("offerNew.voiceTitle")}
          >
            <Mic size={18} className="text-[#00BFFF]" /> {t("offerNew.voiceBtn")}
          </button>
          <button
            type="button"
            onClick={()=>setAiFullOpen(true)}
            className="bg-gradient-to-r from-[#00BFFF] to-[#FBBF24] text-white font-semibold px-5 py-3 flex items-center gap-2 hover:opacity-90 transition-opacity"
            data-testid="ai-generate-full-btn"
          >
            <Zap size={18}/> {t("offerNew.genAll")}
          </button>
        </div>
      </div>

      {/* Client + Project */}
      {transcript && (
        <div className="mt-6 card-flat p-4 flex items-start gap-3 border-l-2 border-[#00BFFF]" data-testid="voice-transcript">
          <Mic size={16} className="text-[#00BFFF] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="overline text-[#00BFFF]">{t("offerNew.transcriptTitle")}</div>
            <p className="text-sm text-[#CBD5E1] mt-1">{transcript}</p>
          </div>
          <button type="button" onClick={() => setTranscript("")} className="text-[#94A3B8] hover:text-[#E2E8F0] shrink-0"><X size={16} /></button>
        </div>
      )}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-6">
          <div className="overline mb-4">{t("offerNew.client")}</div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("offerNew.clientName")}</label>
              <input data-testid="client-name" required className="input-flat mt-1" value={client_name} onChange={(e)=>setClient(e.target.value)}/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("offerNew.clientCompany")}</label>
              <input data-testid="client-company" className="input-flat mt-1" value={client_company} onChange={(e)=>setClientCompany(e.target.value)}/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("offerNew.clientAddress")}</label>
              <input data-testid="client-address" className="input-flat mt-1" value={client_address} onChange={(e)=>setClientAddress(e.target.value)}/>
            </div>
          </div>
        </div>
        <div className="card-flat p-6">
          <div className="overline mb-4">{t("offerNew.project")}</div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("offerNew.projectTitle")}</label>
              <input data-testid="project-title" required className="input-flat mt-1" value={project_title} onChange={(e)=>setProject(e.target.value)} placeholder={t("offerNew.projectPh")}/>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("offerNew.introLabel")}</label>
                <button type="button" onClick={generateAI} disabled={aiBusy} className="text-xs font-semibold text-[#00BFFF] hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="ai-generate-btn">
                  {aiBusy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                  {aiBusy ? t("offerNew.generating") : t("offerNew.genAI")}
                </button>
              </div>
              <textarea data-testid="ai-intro" rows={4} className="input-flat mt-1" value={ai_intro} onChange={(e)=>setAiIntro(e.target.value)} placeholder={t("offerNew.introPh")}/>
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="mt-6 card-flat p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">{t("offerNew.itemsTitle")}</div>
            <div className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
              <Wand2 size={12} className="text-[#00BFFF]"/> {t("offerNew.itemsHint")}
            </div>
          </div>
          <button type="button" onClick={addItem} className="btn-outline flex items-center gap-2 py-2 px-3 text-sm" data-testid="add-item-btn">
            <Plus size={14}/> {t("offerNew.addItem")}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E293B] text-left text-xs uppercase tracking-wider text-[#94A3B8]">
                <th className="py-2 pr-2 w-2/5">{t("offerNew.thDesc")}</th>
                <th className="py-2 pr-2">{t("offerNew.thCat")}</th>
                <th className="py-2 pr-2 w-20">{t("offerNew.thQty")}</th>
                <th className="py-2 pr-2 w-20">{t("offerNew.thUnit")}</th>
                <th className="py-2 pr-2 w-28 text-right">{t("offerNew.thUnitPrice")}</th>
                <th className="py-2 pr-2 w-28 text-right">{t("offerNew.thTotal")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const t = (Number(it.quantity)||0) * (Number(it.unit_price)||0);
                return (
                  <tr key={idx} className="border-b border-[#1E293B]" data-testid={`item-row-${idx}`}>
                    <td className="py-2 pr-2">
                      <div className="flex gap-1">
                        <input className="input-flat flex-1" value={it.description} onChange={(e)=>updateItem(idx, "description", e.target.value)} placeholder={t("offerNew.itemDescPh")} data-testid={`item-description-${idx}`}/>
                        <button
                          type="button"
                          onClick={()=>aiRewriteItem(idx)}
                          disabled={aiItemBusy === idx || !it.description}
                          className="px-2 border border-[#1E293B] hover:bg-[#00BFFF] hover:text-white hover:border-[#00BFFF] disabled:opacity-40 transition-colors flex items-center justify-center"
                          title={t("offerNew.rewriteAI")}
                          data-testid={`ai-item-${idx}`}
                        >
                          {aiItemBusy === idx ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
                        </button>
                      </div>
                    </td>
                    <td className="py-2 pr-2">
                      <select className="input-flat" value={it.category} onChange={(e)=>updateItem(idx, "category", e.target.value)}>
                        {CATEGORY_IDS.map(c => <option key={c} value={c}>{catLabel[c]}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" step="0.01" className="input-flat font-mono-plex" value={it.quantity} onChange={(e)=>updateItem(idx, "quantity", e.target.value)}/>
                    </td>
                    <td className="py-2 pr-2">
                      <input className="input-flat" value={it.unit} onChange={(e)=>updateItem(idx, "unit", e.target.value)}/>
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" step="0.01" className="input-flat font-mono-plex text-right" value={it.unit_price} onChange={(e)=>updateItem(idx, "unit_price", e.target.value)}/>
                    </td>
                    <td className="py-2 pr-2 text-right font-mono-plex">{t.toFixed(2)}</td>
                    <td className="py-2">
                      <button type="button" onClick={()=>removeItem(idx)} className="p-2 text-[#EF4444] hover:bg-red-50" data-testid={`remove-item-${idx}`}>
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals + Notes */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-flat p-6 lg:col-span-2">
          <div className="overline mb-4">{t("offerNew.notesTitle")}</div>
          <textarea rows={5} className="input-flat" value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder={t("offerNew.notesPh")} data-testid="offer-notes"/>
        </div>
        <div className="card-flat p-6">
          <div className="overline mb-4">{t("offerNew.totalsTitle")}</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#94A3B8]">{t("offerNew.subtotal")}</span><span className="font-mono-plex">{totals.subtotal.toFixed(2)} {cur}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-[#94A3B8]">{t("offerNew.markupPct")}</span>
              <input type="number" step="0.1" className="input-flat w-24 text-right font-mono-plex" value={markup_percent} onChange={(e)=>setMarkup(e.target.value)} data-testid="markup-input"/>
            </div>
            <div className="flex justify-between"><span className="text-[#94A3B8]">{t("offerNew.markup")}</span><span className="font-mono-plex">{totals.markup.toFixed(2)} {cur}</span></div>
            <div className="flex justify-between font-semibold border-t border-[#1E293B] pt-2"><span>{t("offerNew.base")}</span><span className="font-mono-plex">{totals.base.toFixed(2)} {cur}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-[#94A3B8]">{t("offerNew.vatPct")}</span>
              <input type="number" step="0.1" className="input-flat w-24 text-right font-mono-plex" value={vat_percent} onChange={(e)=>setVat(e.target.value)} data-testid="vat-input"/>
            </div>
            <div className="flex justify-between"><span className="text-[#94A3B8]">{t("offerNew.vat")}</span><span className="font-mono-plex">{totals.vat.toFixed(2)} {cur}</span></div>
          </div>
          <div className="mt-5 bg-[#111C30] text-white p-4 flex justify-between items-center">
            <span className="font-display font-bold">{t("offerNew.total")}</span>
            <span className="font-display font-extrabold text-2xl" data-testid="offer-total">{totals.total.toFixed(2)}</span>
          </div>
          <div className="text-xs text-[#94A3B8] text-right mt-1">{cur} {t("offerNew.withVat")}</div>
        </div>
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <button type="button" onClick={()=>nav("/app/oferte")} className="btn-outline">{t("offerNew.cancel")}</button>
        <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-offer-btn">
          {saving ? t("offerNew.saving") : t("offerNew.save")}
        </button>
      </div>

      {/* AI Generate Full Offer Modal */}
      {aiFullOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" data-testid="ai-full-modal" onClick={()=>!aiFullBusy && setAiFullOpen(false)}>
          <div onClick={(e)=>e.stopPropagation()}
            style={{ maxHeight: viewportH ? `${viewportH}px` : undefined }}
            className="bg-[#0B111D] border border-[#1E293B] w-full max-w-xl flex flex-col max-h-[92dvh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-none overflow-hidden">
            <div className="p-5 border-b border-[#1E293B] bg-gradient-to-r from-[#00BFFF] to-[#FBBF24] text-white shrink-0">
              <div className="overline" style={{color: "white", opacity: 0.85}}>Powered by GPT-5.2</div>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold mt-1 flex items-center gap-2">
                <Zap size={22}/> {t("offerNew.aiFullTitle")}
              </h2>
              <p className="text-sm text-white/90 mt-2 hidden sm:block">{t("offerNew.aiFullDesc")}</p>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="overline block mb-2">{t("offerNew.templatesLabel")}</label>
                <div className="flex flex-wrap gap-2" data-testid="ai-templates">
                  {[
                    { l: "Renovare baie", t: "Renovare completă baie 6mp: demolare, hidroizolație, montaj gresie și faianță de gamă medie, instalație sanitară nouă (vas WC, lavoar, baterie, cabină duș), manoperă inclusă, evacuare moloz." },
                    { l: "Zugrăvit apartament", t: "Zugrăvit apartament 3 camere (~70mp): pregătire pereți (glet, șlefuire, amorsă), aplicare 2 straturi vopsea lavabilă albă, protecție mobilier și pardoseală, manoperă și materiale incluse." },
                    { l: "Gresie & faianță", t: "Montaj gresie și faianță 25mp: pregătire suprafață, adeziv flexibil, montaj, chituire rosturi, profile de colț, manoperă inclusă, materiale de gamă medie." },
                    { l: "Instalație electrică", t: "Refacere instalație electrică apartament 2 camere: trasee noi, tablou electric cu siguranțe automate, prize și întrerupătoare, spoturi LED, manoperă și materiale incluse." },
                    { l: "Acoperiș / tablă", t: "Montaj acoperiș tip șarpantă, 80mp: structură lemn, folie anticondens, astereală, învelitoare tablă tip țiglă, jgheaburi și burlane, manoperă inclusă." },
                    { l: "Gard / împrejmuire", t: "Construire gard 30ml: fundație beton, stâlpi metalici, panouri bordurate, o poartă pietonală și una auto, manoperă și materiale incluse." },
                  ].map((tpl) => (
                    <button key={tpl.l} type="button" data-testid={`ai-template-${tpl.l}`}
                      onClick={() => setAiBrief(tpl.t)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#1E293B] bg-[#0B111D] text-[#94A3B8] hover:text-[#00BFFF] hover:border-[#00BFFF] transition-colors">
                      {tpl.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t("offerNew.briefLabel")}</label>
                <textarea
                  rows={4}
                  value={aiBrief}
                  onChange={(e)=>setAiBrief(e.target.value)}
                  placeholder={t("offerNew.briefPh")}
                  className="input-flat"
                  data-testid="ai-brief-input"
                />
                <p className="text-xs text-[#94A3B8] mt-2">{t("offerNew.tipText")}</p>
              </div>
              <div className="text-xs text-[#94A3B8] bg-[#0B111D] p-3 border-l-2 border-[#00BFFF]">
                {t("offerNew.warnText")}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-[#1E293B] shrink-0 bg-[#0B111D] safe-pb">
              <button type="button" onClick={()=>setAiFullOpen(false)} disabled={aiFullBusy} className="btn-outline">{t("offerNew.cancel")}</button>
              <button type="button" onClick={aiGenerateFullOffer} disabled={aiFullBusy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="ai-generate-confirm-btn">
                {aiFullBusy ? <><Loader2 size={16} className="animate-spin"/> {t("offerNew.generatingFull")}</> : <><Zap size={16}/> {t("offerNew.genNow")}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice-to-Offer Modal */}
      {voiceOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" data-testid="voice-modal" onClick={() => !recording && !processing && closeVoice()}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0B111D] border border-[#1E293B] w-full max-w-md flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden">
            <div className="p-5 border-b border-[#1E293B] flex items-center justify-between shrink-0">
              <div>
                <div className="overline text-[#00BFFF]">{t("offerNew.voiceHeader")}</div>
                <h2 className="font-display text-xl font-extrabold mt-1 flex items-center gap-2"><Mic size={20} className="text-[#00BFFF]" /> {t("offerNew.voiceBtn")}</h2>
              </div>
              <button type="button" onClick={closeVoice} disabled={recording || processing} className="text-[#94A3B8] hover:text-[#E2E8F0] disabled:opacity-40"><X size={18} /></button>
            </div>
            <div className="p-8 flex flex-col items-center text-center">
              {processing ? (
                <>
                  <Loader2 size={44} className="animate-spin text-[#00BFFF]" />
                  <p className="text-sm text-[#94A3B8] mt-4">{t("offerNew.voiceProcessing")}</p>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={recording ? stopRec : startRec}
                    data-testid="voice-record-toggle"
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${recording ? "bg-[#EF4444] animate-pulse" : "bg-[#00BFFF] hover:scale-105"}`}
                  >
                    {recording ? <Square size={34} className="text-white" fill="white" /> : <Mic size={40} className="text-[#03070E]" />}
                  </button>
                  <div className="mt-5 font-mono-plex text-2xl font-bold" data-testid="voice-timer">{fmtSec(seconds)}</div>
                  <p className="text-sm text-[#94A3B8] mt-2 max-w-xs">
                    {recording ? t("offerNew.voiceRecording") : t("offerNew.voiceIdle")}
                  </p>
                </>
              )}
            </div>
            <div className="px-5 pb-4 text-xs text-[#94A3B8] bg-[#0B111D] border-t border-[#1E293B] pt-3 safe-pb">
              {t("offerNew.voiceWarn")}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
